import type {
  AdminDashboardOrganizationRow,
  AdminUnpaidInvoiceRow,
} from "@/admin/entities/adminDashboard";
// Напрямую из routes: баррель тянет config с import.meta, который не собирается в jest.
import { PagePaths } from "@/admin/shared/constants/routes";
import { computeTrend } from "./dashboardFormat";

export type OrganizationSortKey =
  | "title"
  | "salesUSD"
  | "revenueShareUSD"
  | "paidInvoicesUSD"
  | "views"
  | "share"
  | "trend";

export type SortDirection = "asc" | "desc";

export interface OrganizationTableRow extends AdminDashboardOrganizationRow {
  /** Доля в нашем ревшере за период, %; null, если ревшера нет. */
  share: number | null;
  /** Тренд продаж к прошлому периоду, %; null без базы или без сравнения. */
  trend: number | null;
}

/** Подпись организации без названия: и в строке, и в сортировке. */
export const UNTITLED_ORGANIZATION = "Без названия";

export const organizationTitle = (title: string): string =>
  title || UNTITLED_ORGANIZATION;

const round1 = (value: number) => Math.round(value * 10) / 10;
const money = (value: number) => Math.round(value * 100) / 100;

export function buildOrganizationRows(
  rows: AdminDashboardOrganizationRow[],
  totalRevenueShareUSD: number,
  hasComparison: boolean
): OrganizationTableRow[] {
  return rows.map((row) => ({
    ...row,
    title: organizationTitle(row.title),
    share:
      totalRevenueShareUSD > 0
        ? round1((row.revenueShareUSD / totalRevenueShareUSD) * 100)
        : null,
    trend: hasComparison
      ? computeTrend(row.salesUSD, row.previousSalesUSD)
      : null,
  }));
}

const NUMERIC: Record<
  Exclude<OrganizationSortKey, "title">,
  (row: OrganizationTableRow) => number | null
> = {
  salesUSD: (row) => row.salesUSD,
  revenueShareUSD: (row) => row.revenueShareUSD,
  paidInvoicesUSD: (row) => row.paidInvoicesUSD,
  views: (row) => row.views,
  share: (row) => row.share,
  trend: (row) => row.trend,
};

/** Сортировка по любой колонке; пустые значения всегда в конце. */
export function sortOrganizationRows(
  rows: OrganizationTableRow[],
  key: OrganizationSortKey,
  direction: SortDirection
): OrganizationTableRow[] {
  const factor = direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    if (key === "title") {
      return factor * left.title.localeCompare(right.title, "ru");
    }
    const leftValue = NUMERIC[key](left);
    const rightValue = NUMERIC[key](right);
    if (leftValue === null && rightValue === null) return 0;
    if (leftValue === null) return 1;
    if (rightValue === null) return -1;
    return factor * (leftValue - rightValue);
  });
}

export function organizationTotals(rows: AdminDashboardOrganizationRow[]): {
  salesUSD: number;
  revenueShareUSD: number;
  paidInvoicesUSD: number;
  views: number;
} {
  return {
    salesUSD: money(rows.reduce((total, row) => total + row.salesUSD, 0)),
    revenueShareUSD: money(
      rows.reduce((total, row) => total + row.revenueShareUSD, 0)
    ),
    paidInvoicesUSD: money(
      rows.reduce((total, row) => total + row.paidInvoicesUSD, 0)
    ),
    views: rows.reduce((total, row) => total + row.views, 0),
  };
}

/** Организация открывается диалогом CRUD по ?tab=details&id=: первый шаг диалога. */
export function organizationHref(organizationId: string): string {
  return `${PagePaths.organizations}?tab=details&id=${encodeURIComponent(organizationId)}`;
}

/**
 * Список платежей по контрактам с фильтром организации и статуса: у
 * списка нет фильтра по id платежа, а пара организация и статус сужает
 * его до одного-двух счетов.
 */
export function unpaidInvoiceHref(
  row: Pick<AdminUnpaidInvoiceRow, "organizationId" | "status">
): string {
  const filter = JSON.stringify({
    organizationId: row.organizationId,
    status: row.status,
  });
  return `${PagePaths.billingPayments}?filter=${encodeURIComponent(filter)}`;
}

export const UNPAID_STATUS_LABEL: Record<string, string> = {
  wait_payment: "Ждём оплату",
  on_way: "Оплата в пути",
};
